// Daily reports — admin space for the per-dealmaker end-of-day report.
//
// Reads GET /api/team/daily-reports?date= (permission: view_team_activity):
// the stored payload per member for one ET day — emails sent, internal /
// external meetings, the engagements that moved (grouped events + a one-line
// summary), overdue tasks and next actions, and the live deals that went
// quiet. The worker generates and sends it at the configured hour
// (backend/apps/api/app/dealmaker_daily.py); this page shows any day,
// regenerates on demand, previews the email, and holds the recipients.
//
// Same payload as the Slack DM and the email, so what the admin sees here
// is exactly what went out.

const DD_TILES = [
  { key: 'emails_sent',       label: 'Emails sent',     hint: 'External emails this person sent (own mailbox, CRM-linked)' },
  { key: 'companies_reached', label: 'Companies',       hint: 'Distinct outside domains emailed' },
  { key: 'internal_meetings', label: 'Internal mtgs',   hint: 'Calendar events with only IndustryPro attendees (not all-day)' },
  { key: 'external_meetings', label: 'External mtgs',   hint: 'Calendar events with at least one outside attendee (not all-day)' },
  { key: 'recorded_meetings', label: 'Recorded',        hint: 'Fireflies-recorded meetings they were on' },
  { key: 'notes_logged',      label: 'Notes',           hint: 'Notes they wrote in Atlas' },
  { key: 'crm_edits',         label: 'CRM edits',       hint: 'Record edits they made in Atlas (stage moves counted once)' },
  { key: 'tasks_completed',   label: 'Tasks done',      hint: 'Tasks they completed' },
];

// Benchmark columns. `bad` = a nonzero value is a red flag (rendered rose).
const DD_BENCH = [
  ['emails_sent', 'Emails'], ['companies_reached', 'Companies'], ['internal_meetings', 'Internal'],
  ['external_meetings', 'External'], ['recorded_meetings', 'Recorded'],
  ['engagements_worked', 'Deals worked'],
  ['waiting', 'Waiting', true, 'Threads where a known contact wrote to them and nobody replied in 2+ days'],
  ['no_followup', 'No follow-up', true, 'Calls in the last 7 days with nothing done on that company 48h later'],
  ['missing_next_action', 'No next action', true, 'Live deals past the D/S call with no next action set'],
  ['stuck', 'Stuck', true, 'Live deals older in their stage than 2× the stage median (min 30 days)'],
  ['owed', 'Owed', true, 'Recorded calls that assigned them action items, 7+ days, no task since'],
  ['overdue_tasks', 'Overdue tasks', true], ['overdue_next_actions', 'Overdue NAs', true],
  ['at_risk_deals', 'Flagged', true, 'Live deals past the D/S call carrying any red flag (quiet, stuck, no/overdue next action) — the stock; full lists on the Book health tab'],
  ['newly_flagged', 'New', true, 'Deals that crossed a line since the previous report'],
  ['cleared', 'Cleared', false, 'Deals that were flagged in the previous report and are not any more'],
  ['at_risk_fees', '$ at risk', true, 'Estimated fees on the flagged deals', 'money'],
];

const ddPrevWeekday = (iso) => {
  let d = ddShiftDay(iso, -1);
  while ([0, 6].includes(new Date(d + 'T12:00:00').getDay())) d = ddShiftDay(d, -1);
  return d;
};

const DDRow = ({ children, onClick, title }) => (
  <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', fontSize: 12.5, padding: '2px 0', flexWrap: 'wrap' }} title={title}>{children}</div>
);
const DDLink = ({ id, kind, children }) => (
  <span onClick={() => (kind === 'company' ? window.AtlasNav?.goToCompany?.(id) : window.AtlasNav?.goToDeal?.(id))}
    style={{ color: 'var(--accent-text)', cursor: id ? 'pointer' : 'default' }}>{children}</span>
);
const DDWhere = ({ x }) => (
  x.engagement_id ? <DDLink id={x.engagement_id}>{x.engagement}</DDLink>
    : x.company_id ? <DDLink id={x.company_id} kind="company">{x.company}</DDLink>
      : <span style={{ color: 'var(--text-4)' }}>{x.company || ''}</span>
);

const DD_KIND = {
  email_out: { label: 'sent',      hue: 'blue' },
  email_in:  { label: 'received',  hue: 'slate' },
  meeting:   { label: 'meeting',   hue: 'violet' },
  recording: { label: 'recorded',  hue: 'violet' },
  stage:     { label: 'stage',     hue: 'green' },
  created:   { label: 'created',   hue: 'green' },
  edit:      { label: 'edit',      hue: 'amber' },
  note:      { label: 'note',      hue: 'teal' },
  task_done: { label: 'task done', hue: 'teal' },
};

const ddTodayIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const ddShiftDay = (iso, n) => {
  const d = new Date(iso + 'T12:00:00');
  d.setDate(d.getDate() + n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const ddLongDate = (iso) => new Date(iso + 'T12:00:00').toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' });
const ddTime = (iso) => (iso ? new Date(iso).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' }) : '');
const ddMoney = (v) => (v == null ? '' : v >= 1e6 ? `$${(v / 1e6).toFixed(1)}M` : v >= 1e3 ? `$${Math.round(v / 1e3)}k` : `$${Math.round(v)}`);
const ddDaysAgo = (iso) => {
  if (!iso) return null;
  return Math.max(0, Math.round((Date.now() - new Date(iso + 'T12:00:00').getTime()) / 86400000));
};

const DDSec = ({ title, count, children, right, help }) => (
  <div style={{ marginTop: 12 }}>
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginBottom: 5 }}>
      <span style={{ fontSize: 10.5, fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--text-4)' }}>{title}</span>
      {help && <window.HelpTip text={help} size={12} style={{ margin: 0, alignSelf: 'center' }} />}
      {count != null && <span style={{ fontSize: 10.5, color: 'var(--text-4)' }}>· {count}</span>}
      <div style={{ flex: 1 }} />
      {right}
    </div>
    {children}
  </div>
);

const DDEvent = ({ ev }) => {
  const k = DD_KIND[ev.kind] || { label: ev.kind, hue: 'slate' };
  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', padding: '2px 0', fontSize: 12 }}>
      <span style={{ fontSize: 10.5, color: 'var(--text-4)', width: 58, flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>{ddTime(ev.at)}</span>
      <Pill hue={k.hue} sz="sm">{k.label}</Pill>
      <span style={{ color: 'var(--text-2)', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={ev.text}>
        {ev.text}
        {ev.who && ev.who.length > 0 && <span style={{ color: 'var(--text-4)' }}> · {ev.who.join(', ')}</span>}
      </span>
    </div>
  );
};

const DDEngagement = ({ g, kind }) => {
  const [open, setOpen] = React.useState(false);
  const isDeal = kind === 'deal';
  const go = () => {
    if (isDeal) window.AtlasNav?.goToDeal?.(g.engagement_id);
    else if (g.company_id) window.AtlasNav?.goToCompany?.(g.company_id);
  };
  const name = isDeal ? (g.name || '(untitled)') : (g.company || 'unnamed company');
  return (
    <div style={{ padding: '7px 0', borderTop: '1px solid var(--border)' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
        <span onClick={go} style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--accent-text)', cursor: (isDeal || g.company_id) ? 'pointer' : 'default' }}>{name}</span>
        {isDeal && g.stage && <Pill hue="slate" sz="sm">{g.stage}</Pill>}
        {isDeal && g.role && g.role !== 'dealmaker' && <Pill hue="amber" sz="sm" title="Not the dealmaker of record on this engagement">{g.role}</Pill>}
        {isDeal && g.fees != null && <span style={{ fontSize: 11, color: 'var(--text-4)' }}>{ddMoney(g.fees)}</span>}
        {isDeal && g.company && <span style={{ fontSize: 11, color: 'var(--text-4)' }}>· {g.company}</span>}
        <div style={{ flex: 1 }} />
        <button onClick={() => setOpen((v) => !v)}
          style={{ fontSize: 11, color: 'var(--text-3)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
          {open ? 'hide' : `${g.event_total ?? (g.events || []).length} event${(g.event_total ?? (g.events || []).length) === 1 ? '' : 's'}`}
        </button>
      </div>
      <div style={{ fontSize: 12.5, color: 'var(--text-2)', marginTop: 2, lineHeight: 1.45 }}>
        {g.summary}
        {g.summary_auto && <span title={`Rule-based line: ${g.summary_auto}`} style={{ marginLeft: 6, fontSize: 10, color: 'var(--text-4)', cursor: 'help' }}>AI</span>}
      </div>
      {open && (
        <div style={{ marginTop: 6, paddingLeft: 4 }}>
          {(g.events || []).map((ev, i) => <DDEvent key={i} ev={ev} />)}
          {g.event_total > (g.events || []).length && (
            <div style={{ fontSize: 11, color: 'var(--text-4)', paddingLeft: 66 }}>… +{g.event_total - g.events.length} more</div>
          )}
        </div>
      )}
    </div>
  );
};

// One line of warnings for a collapsed card — only what is nonzero.
const ddWarnBits = (c) => {
  const bits = [];
  if (c.waiting) bits.push(`${c.waiting} waiting`);
  if (c.no_followup) bits.push(`${c.no_followup} no follow-up`);
  const overdue = (c.overdue_tasks || 0) + (c.overdue_next_actions || 0);
  if (overdue) bits.push(`${overdue} overdue`);
  if (c.owed) bits.push(`${c.owed} owed`);
  if (c.newly_flagged) bits.push(`${c.newly_flagged} newly flagged`);
  if (c.at_risk_fees) bits.push(`${ddMoney(c.at_risk_fees)} at risk`);
  return bits;
};

const DDMemberCard = ({ r, onRecompute, busy, open, onToggle }) => {
  const c = r.counts || {};
  const m = r.member || {};
  const [showMail, setShowMail] = React.useState(false);
  const [showMeetings, setShowMeetings] = React.useState(false);
  const [showAllOthers, setShowAllOthers] = React.useState(false);
  const quietPct = c.live_engagements ? Math.round(100 * (c.quiet_deals || 0) / c.live_engagements) : null;
  const warn = ddWarnBits(c);
  const header = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', cursor: 'pointer' }} onClick={onToggle}>
      <span style={{ fontSize: 11, color: 'var(--text-4)', width: 10, display: 'inline-block' }}>{open ? '▾' : '▸'}</span>
      <span onClick={(e) => { e.stopPropagation(); window.AtlasNav?.goToUser?.(m.id, m.name); }}
        style={{ fontSize: 14, fontWeight: 650, color: 'var(--text)', cursor: 'pointer' }}>{m.name}</span>
      {m.role && m.role !== 'dealmaker' && <span style={{ fontSize: 10.5, color: 'var(--text-4)' }}>{(m.role || '').replace('_', ' ')}</span>}
      {m.team_name && <span style={{ fontSize: 11, color: 'var(--text-4)' }}>{m.team_name}</span>}
      {!open && (
        <span style={{ fontSize: 12, color: 'var(--text-2)' }}>
          · {c.emails_sent ?? 0} emails · {c.external_meetings ?? 0} ext mtg{c.recorded_meetings ? ` · ${c.recorded_meetings} rec` : ''} · {c.engagements_worked ?? 0} deals worked
          {warn.length > 0 && <span style={{ color: HUE.rose.dot }}> · ⚠ {warn.join(' · ')}</span>}
        </span>
      )}
      {open && <span style={{ fontSize: 11, color: 'var(--text-4)' }}>· {c.live_engagements ?? 0} live engagements</span>}
      {r.sync_note && <Pill hue="rose" sz="sm" dot title={r.sync_note}>sync issue</Pill>}
      <div style={{ flex: 1 }} />
      {r.sent_at && open && <span style={{ fontSize: 10.5, color: 'var(--text-4)' }} title={r.sent_at}>sent {ddTime(r.sent_at)}</span>}
      {open && <Btn size="xs" variant="ghost" disabled={busy} onClick={(e) => { e.stopPropagation(); onRecompute(m.id); }} title="Recompute this person for this day">Recompute</Btn>}
    </div>
  );
  if (!open) {
    return <div style={{ border: '1px solid var(--border)', borderRadius: 8, background: 'var(--surface)', padding: '9px 14px', marginBottom: 6 }}>{header}</div>;
  }
  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 8, background: 'var(--surface)', padding: '12px 14px', marginBottom: 12 }}>
      {header}
      {r.sync_note && <div style={{ fontSize: 11.5, color: HUE.rose.dot, marginTop: 4 }}>{r.sync_note}</div>}
      {(c.at_risk_deals || 0) > 0 && (
        <div style={{ fontSize: 12, color: 'var(--text-2)', marginTop: 6 }} title="Live deals past the D/S call with no next action, an overdue next action, stuck in stage, or quiet 30+ days — each counted once">
          <span style={{ color: HUE.rose.dot, fontWeight: 600 }}>{ddMoney(c.at_risk_fees)} at risk</span>
          <span style={{ color: 'var(--text-4)' }}> · {c.at_risk_deals} live deal{c.at_risk_deals === 1 ? '' : 's'} carrying a red flag</span>
        </div>
      )}

      <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', marginTop: 10 }}>
        {DD_TILES.map(({ key, label, hint }) => (
          <div key={key} title={hint + (r.usual && r.usual[key] != null ? ` · usual weekday over the last ${r.usual.weeks} weeks: ${r.usual[key]}` : '')} style={{ minWidth: 64 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 5 }}>
              <span style={{ fontSize: 17, fontWeight: 650, color: (c[key] || 0) === 0 ? 'var(--text-4)' : 'var(--text)', fontVariantNumeric: 'tabular-nums' }}>{c[key] ?? 0}</span>
              {r.usual && r.usual[key] != null && (
                <span style={{ fontSize: 10, color: (c[key] || 0) >= r.usual[key] ? 'var(--text-4)' : HUE.amber.dot, fontVariantNumeric: 'tabular-nums' }}
                  title={`Usual weekday: ${r.usual[key]}`}>/ {r.usual[key]}</span>
              )}
            </div>
            <div style={{ fontSize: 9.5, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--text-4)', textDecoration: 'underline dotted var(--border-strong)', textUnderlineOffset: 3, cursor: 'help', width: 'fit-content' }}>{label}</div>
          </div>
        ))}
      </div>

      <DDSec title="Engagements worked" count={(r.engagements || []).length}>
        {(r.engagements || []).length === 0 && (
          <div style={{ fontSize: 12.5, color: 'var(--text-3)' }}>No live engagement was touched.</div>
        )}
        {(r.engagements || []).map((g) => <DDEngagement key={g.engagement_id} g={g} kind="deal" />)}
      </DDSec>

      {(r.other_companies || []).length > 0 && (
        <DDSec title="Also touched — no live engagement" count={r.other_companies.length}
          right={r.other_companies.length > 5 && (
            <button onClick={() => setShowAllOthers((v) => !v)} style={{ fontSize: 11, color: 'var(--text-3)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
              {showAllOthers ? 'show fewer' : `show all ${r.other_companies.length}`}
            </button>
          )}>
          {(showAllOthers ? r.other_companies : r.other_companies.slice(0, 5)).map((o) => <DDEngagement key={o.company_id} g={o} kind="company" />)}
        </DDSec>
      )}

      {(r.waiting || []).length > 0 && (
        <DDSec title="Waiting on them" count={c.waiting}
          help="A known contact wrote to them and nobody at IndustryPro replied in 2+ days.">
          {r.waiting.map((w, i) => (
            <DDRow key={i}>
              <span style={{ color: 'var(--text-3)', fontSize: 11.5 }}>{w.who}</span>
              {w.link ? <a href={w.link} target="_blank" rel="noopener" style={{ color: 'var(--text-2)', textDecoration: 'none' }} title={w.subject}>{w.subject}</a>
                : <span style={{ color: 'var(--text-2)' }} title={w.subject}>{w.subject}</span>}
              <DDWhere x={w} />
              {w.stage && <Pill hue="slate" sz="sm">{w.stage}</Pill>}
              {w.fees != null && <span style={{ fontSize: 11, color: 'var(--text-4)' }}>{ddMoney(w.fees)}</span>}
              <span style={{ fontSize: 11, color: (w.days_waiting || 0) >= 5 ? HUE.rose.dot : HUE.amber.dot }}>{w.days_waiting}d waiting</span>
            </DDRow>
          ))}
          {(c.waiting || 0) > r.waiting.length && <div style={{ fontSize: 11, color: 'var(--text-4)' }}>… +{c.waiting - r.waiting.length} more</div>}
        </DDSec>
      )}

      {(r.no_followup || []).length > 0 && (
        <DDSec title="Calls with no follow-up" count={c.no_followup}
          help="Met in the last 7 days, and 48 hours later there is no email, note, stage move, edit or task on that company.">
          {r.no_followup.map((f, i) => (
            <DDRow key={i}>
              <Pill hue={f.kind === 'recording' ? 'violet' : 'blue'} sz="sm">{f.kind}</Pill>
              <span style={{ color: 'var(--text-2)' }} title={f.title}>{f.title}</span>
              <DDWhere x={f} />
              {f.stage && <Pill hue="slate" sz="sm">{f.stage}</Pill>}
              {f.fees != null && <span style={{ fontSize: 11, color: 'var(--text-4)' }}>{ddMoney(f.fees)}</span>}
              <span style={{ fontSize: 11, color: HUE.amber.dot }}>{f.days_ago}d ago</span>
            </DDRow>
          ))}
        </DDSec>
      )}

      {((c.at_risk_deals || 0) > 0 || (r.newly_flagged || []).length > 0 || (r.cleared || []).length > 0) && (
        <DDSec title="Book" count={`${c.at_risk_deals || 0} flagged${c.at_risk_fees ? ` · ${ddMoney(c.at_risk_fees)} at risk` : ''}`}
          right={<span style={{ fontSize: 10.5, color: 'var(--text-4)' }}>{c.quiet_deals || 0} quiet · {c.stuck || 0} stuck · {c.missing_next_action || 0} without a next action — full lists on the Book health tab</span>}>
          {(r.newly_flagged || []).map((d) => (
            <DDRow key={'n' + d.engagement_id}>
              <Pill hue="rose" sz="sm" dot>new</Pill>
              <DDLink id={d.engagement_id}>{d.name}</DDLink>
              <span style={{ color: 'var(--text-3)' }}>{d.label}</span>
              {d.stage && <Pill hue="slate" sz="sm">{d.stage}</Pill>}
              {d.fees != null && <span style={{ fontSize: 11.5, color: 'var(--text-3)' }}>{ddMoney(d.fees)}</span>}
            </DDRow>
          ))}
          {(c.newly_flagged || 0) > (r.newly_flagged || []).length && <div style={{ fontSize: 11, color: 'var(--text-4)' }}>… +{c.newly_flagged - r.newly_flagged.length} more newly flagged</div>}
          {(r.cleared || []).map((d) => (
            <DDRow key={'c' + d.engagement_id}>
              <Pill hue="green" sz="sm" dot>cleared</Pill>
              <DDLink id={d.engagement_id}>{d.name}</DDLink>
              <span style={{ color: 'var(--text-3)' }}>no longer {d.label}</span>
            </DDRow>
          ))}
          {(r.newly_flagged || []).length === 0 && (r.cleared || []).length === 0 && (
            <div style={{ fontSize: 12, color: 'var(--text-4)' }}>{r.previous_report_date ? 'Nothing crossed a line since the previous report.' : 'First report for this person — nothing to compare against yet.'}</div>
          )}
        </DDSec>
      )}

      {r.cleanup && ((r.cleanup.since_start || {}).engagements_touched > 0 || (r.cleanup.today || {}).engagements_touched > 0) && (
        <DDSec title="Cleanup" help="Engagement edits made in Atlas — holds, exits, next actions, stage moves, reassignments." count={`${(r.cleanup.since_start || {}).engagements_touched || 0} engagements since ${window.fmtDate(r.cleanup.since)}`}>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 12.5 }}>
            {[['engagements_touched', 'updated'], ['holds', 'held'], ['exits', 'exited'], ['next_actions_set', 'next actions set'], ['stage_moves', 'stage moves'], ['reassigned', 'reassigned'], ['archived', 'archived']].map(([k, l]) => (
              <span key={k} title={`${l}: this report / since ${window.fmtDate(r.cleanup.since)}`}>
                <b style={{ color: 'var(--text)' }}>{(r.cleanup.today || {})[k] || 0}</b>
                <span style={{ color: 'var(--text-4)' }}> / {(r.cleanup.since_start || {})[k] || 0} {l}</span>
              </span>
            ))}
          </div>
        </DDSec>
      )}

      {(r.owed || []).length > 0 && (
        <DDSec title="Owed action items" count={c.owed}
          help="Recorded calls that assigned them something 7+ days ago, with no task created on that company since.">
          {r.owed.map((o) => (
            <div key={o.note_id} style={{ padding: '3px 0' }}>
              <DDRow>
                <span style={{ color: 'var(--text-2)', fontWeight: 600 }}>{o.title}</span>
                <DDWhere x={o} />
                <span style={{ fontSize: 11, color: HUE.amber.dot }}>{o.days_ago}d ago</span>
              </DDRow>
              {(o.items || []).map((it, i) => <div key={i} style={{ fontSize: 12, color: 'var(--text-3)', paddingLeft: 12 }}>· {it}</div>)}
            </div>
          ))}
        </DDSec>
      )}

      {((c.overdue_tasks || 0) > 0 || (c.overdue_next_actions || 0) > 0) && (
        <DDSec title="Overdue" count={`${c.overdue_tasks || 0} task${c.overdue_tasks === 1 ? '' : 's'} · ${c.overdue_next_actions || 0} next action${c.overdue_next_actions === 1 ? '' : 's'}`}>
          {(r.overdue_tasks || []).map((t) => (
            <div key={t.id} style={{ display: 'flex', gap: 8, alignItems: 'baseline', fontSize: 12.5, padding: '2px 0' }}>
              {t.priority && (t.priority === 'urgent' || t.priority === 'high') && <Pill hue={t.priority === 'urgent' ? 'rose' : 'amber'} sz="sm">{t.priority}</Pill>}
              <span style={{ color: 'var(--text-2)' }}>{t.title}</span>
              <span style={{ fontSize: 11, color: HUE.rose.dot }}>due {window.fmtDate(t.due)}{ddDaysAgo(t.due) != null ? ` · ${ddDaysAgo(t.due)}d late` : ''}</span>
              {(t.engagement_id || t.company_id) && (
                <span onClick={() => (t.engagement_id ? window.AtlasNav?.goToDeal?.(t.engagement_id) : window.AtlasNav?.goToCompany?.(t.company_id))}
                  style={{ fontSize: 11, color: 'var(--accent-text)', cursor: 'pointer' }}>{t.engagement || t.company}</span>
              )}
            </div>
          ))}
          {(c.overdue_tasks || 0) > (r.overdue_tasks || []).length && (
            <div style={{ fontSize: 11, color: 'var(--text-4)' }}>… +{c.overdue_tasks - r.overdue_tasks.length} more overdue tasks</div>
          )}
          {(r.overdue_next_actions || []).map((na) => (
            <div key={na.engagement_id} style={{ display: 'flex', gap: 8, alignItems: 'baseline', fontSize: 12.5, padding: '2px 0' }}>
              <Pill hue="slate" sz="sm">next action</Pill>
              <span onClick={() => window.AtlasNav?.goToDeal?.(na.engagement_id)} style={{ color: 'var(--accent-text)', cursor: 'pointer' }}>{na.name}</span>
              <span style={{ fontSize: 11, color: HUE.rose.dot }}>{window.fmtDate(na.date)}{ddDaysAgo(na.date) != null ? ` · ${ddDaysAgo(na.date)}d late` : ''}</span>
              {na.notes && <span style={{ fontSize: 11.5, color: 'var(--text-3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{na.notes}</span>}
            </div>
          ))}
        </DDSec>
      )}

      {(r.meetings || []).length > 0 && (
        <DDSec title="Meetings" count={r.meetings.length}
          right={<button onClick={() => setShowMeetings((v) => !v)} style={{ fontSize: 11, color: 'var(--text-3)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>{showMeetings ? 'hide' : 'show'}</button>}>
          {showMeetings && r.meetings.map((mt, i) => (
            <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'baseline', fontSize: 12.5, padding: '2px 0' }}>
              <span style={{ fontSize: 10.5, color: 'var(--text-4)', width: 58, flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>{ddTime(mt.at)}</span>
              <Pill hue={mt.kind === 'internal' ? 'slate' : mt.kind === 'recorded' ? 'violet' : 'blue'} sz="sm">{mt.kind}</Pill>
              <span style={{ color: 'var(--text-2)', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={mt.summary || mt.title}>{mt.title}</span>
              {mt.company_id && <span onClick={() => window.AtlasNav?.goToCompany?.(mt.company_id)} style={{ fontSize: 11, color: 'var(--accent-text)', cursor: 'pointer', whiteSpace: 'nowrap' }}>{mt.company}</span>}
              {mt.who && mt.who.length > 0 && <span style={{ fontSize: 11, color: 'var(--text-4)', whiteSpace: 'nowrap' }}>{mt.who.join(', ')}</span>}
            </div>
          ))}
        </DDSec>
      )}

      {(r.emails || []).length > 0 && (
        <DDSec title="Emails sent" count={c.emails_sent}
          right={<button onClick={() => setShowMail((v) => !v)} style={{ fontSize: 11, color: 'var(--text-3)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>{showMail ? 'hide' : 'show'}</button>}>
          {showMail && r.emails.map((e, i) => (
            <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'baseline', fontSize: 12.5, padding: '2px 0' }}>
              <span style={{ fontSize: 10.5, color: 'var(--text-4)', width: 58, flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>{ddTime(e.at)}</span>
              <span style={{ color: 'var(--text-2)', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={e.subject}>{e.subject}</span>
              <span style={{ fontSize: 11, color: 'var(--text-4)', whiteSpace: 'nowrap' }}>→ {(e.to || []).join(', ')}</span>
              {e.company_id && <span onClick={() => window.AtlasNav?.goToCompany?.(e.company_id)} style={{ fontSize: 11, color: 'var(--accent-text)', cursor: 'pointer', whiteSpace: 'nowrap' }}>{e.company}</span>}
            </div>
          ))}
          {showMail && c.emails_sent > r.emails.length && <div style={{ fontSize: 11, color: 'var(--text-4)' }}>… +{c.emails_sent - r.emails.length} more</div>}
        </DDSec>
      )}
    </div>
  );
};

// Recipients + schedule — one JSON row in app_settings (manage_members).
const DDConfigDrawer = ({ open, onClose, initial, emailStatus, state, onSaved }) => {
  const [members, setMembers] = React.useState('');
  const [notify, setNotify] = React.useState('');
  const [emailTo, setEmailTo] = React.useState('');
  const [hour, setHour] = React.useState(19);
  const [escalate, setEscalate] = React.useState(true);
  const [escalateHours, setEscalateHours] = React.useState(48);
  const [cleanupSince, setCleanupSince] = React.useState('2026-09-01');
  const [cleanupMembers, setCleanupMembers] = React.useState('');
  const [cleanupDeactivated, setCleanupDeactivated] = React.useState('');
  const [err, setErr] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  React.useEffect(() => {
    if (!open || !initial) return;
    setMembers((initial.members || []).join('\n'));
    setNotify((initial.notify || []).join('\n'));
    setEmailTo((initial.email_to || []).join('\n'));
    setHour(initial.hour_et ?? 19);
    setEscalate(initial.escalate !== false);
    setEscalateHours(initial.escalate_after_hours ?? 48);
    setCleanupSince(initial.cleanup_since || '2026-09-01');
    setCleanupMembers((initial.cleanup_members || []).join('\n'));
    setCleanupDeactivated((initial.cleanup_deactivated || []).join('\n'));
    setErr('');
  }, [open, initial]);
  if (!open) return null;
  const lines = (s) => s.split(/[\n,;]+/).map((x) => x.trim()).filter(Boolean);
  const save = async () => {
    setSaving(true); setErr('');
    try {
      const cfg = await window.AtlasApi.dailyReportsSaveConfig({
        members: lines(members), notify: lines(notify), email_to: lines(emailTo), hour_et: Number(hour),
        escalate: !!escalate, escalate_after_hours: Number(escalateHours) || 48, cleanup_since: cleanupSince,
        cleanup_members: lines(cleanupMembers), cleanup_deactivated: lines(cleanupDeactivated),
      });
      onSaved(cfg);
      onClose();
    } catch (e) { setErr(e?.detail || e?.message || 'Save failed'); } finally { setSaving(false); }
  };
  const ta = (v, set, ph) => (
    <textarea value={v} onChange={(e) => set(e.target.value)} rows={3} placeholder={ph}
      style={{ width: '100%', fontSize: 12.5, padding: '6px 8px', border: '1px solid var(--border)', borderRadius: 5, background: 'var(--surface)', color: 'var(--text)', fontFamily: 'inherit', resize: 'vertical' }} />
  );
  const hours = Array.from({ length: 24 }, (_, h) => h);
  const fmtH = (h) => `${((h + 11) % 12) + 1}:00 ${h < 12 ? 'AM' : 'PM'} ET`;
  return (
    <window.Drawer open onClose={onClose} width={480} title="Daily report settings"
      footer={<div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <Btn size="sm" variant="ghost" onClick={onClose}>Cancel</Btn>
        <Btn size="sm" variant="accent" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Btn>
      </div>}>
      <div style={{ padding: 14 }}>
        <Field label="Dealmakers covered" hint="Team-member emails, one per line. Leave empty to cover every active dealmaker and senior associate.">
          {ta(members, setMembers, 'kmiller@industrypro.com\ntklewer@industrypro.com')}
        </Field>
        <Field label="Slack recipients" hint="Team-member emails; each needs a Slack ID on their profile. The report DMs them at the send hour.">
          {ta(notify, setNotify, 'zzirkle@industrypro.com')}
        </Field>
        <Field label="Email recipients" hint={emailStatus || ''}>
          {ta(emailTo, setEmailTo, 'zzirkle@industrypro.com')}
        </Field>
        <Field label="Cleanup roster" hint="Team-member emails, one per line — the people doing the cleaning (Cleanup tab, top list).">
          {ta(cleanupMembers, setCleanupMembers, 'kmiller@industrypro.com\ntklewer@industrypro.com')}
        </Field>
        <Field label="Deactivated dealmakers" hint="Team-member emails whose books are being cleaned FOR them (Cleanup tab, second list).">
          {ta(cleanupDeactivated, setCleanupDeactivated, 'dstaffieri@industrypro.com\ncdowless@industrypro.com')}
        </Field>
        <Field label="Cleanup mission start" hint="Cleanup counts (engagements held, exited, given a next action…) run from this date, on the Cleanup tab and in each report.">
          <input type="date" value={cleanupSince} onChange={(e) => e.target.value && setCleanupSince(e.target.value)}
            style={{ fontSize: 12.5, padding: '6px 8px', border: '1px solid var(--border)', borderRadius: 5, background: 'var(--surface)', color: 'var(--text)' }} />
        </Field>
        <Field label="Send hour (weekdays)" hint="Sent each weekday morning at this hour, covering the previous weekday (Monday's report covers Friday through Sunday).">
          <window.Picker value={hour} onChange={setHour} placeholder="Send hour" minWidth={120}
            options={hours.map((h) => ({ value: h, label: fmtH(h) }))} />
        </Field>
        <Field label="Real-time escalation" hint="Slack DM to the recipients above the moment a known contact's email on a Data Gathering / Valuation Presented / LOI deal has gone unanswered this long. Once per thread.">
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 12.5, color: 'var(--text-2)' }}>
              <input type="checkbox" checked={escalate} onChange={(e) => setEscalate(e.target.checked)} /> On
            </label>
            <window.Picker value={escalateHours} onChange={setEscalateHours} disabled={!escalate} placeholder="Escalate after" minWidth={110}
              options={[24, 48, 72, 96, 120, 168].map((h) => ({ value: h, label: h < 48 ? `${h} hours` : `${h / 24} days` }))} />
          </div>
        </Field>
        {state && state.last_sent && (
          <div style={{ fontSize: 11.5, color: 'var(--text-3)', marginTop: 8, lineHeight: 1.5 }}>
            Last automatic send: {window.fmtDate(state.last_sent)} — {state.members ?? 0} member{state.members === 1 ? '' : 's'}, {state.slack_sent ?? 0} Slack DM{state.slack_sent === 1 ? '' : 's'}, {state.email_sent ?? 0} email{state.email_sent === 1 ? '' : 's'}.
            {(state.failures || []).length > 0 && <div style={{ color: HUE.rose.dot }}>Failures: {state.failures.join(' · ')}</div>}
          </div>
        )}
        {err && <div style={{ fontSize: 12.5, color: HUE.rose.dot, marginTop: 10 }}>{err}</div>}
      </div>
    </window.Drawer>
  );
};

// Book health tab — the stock lists (quiet / stuck / no next action) by age band, per member.
const DD_BANDS = [[0, 90, '30–90 days'], [90, 180, '90–180 days'], [180, 1e9, 'over 180 days']];
const ddBand = (d) => (DD_BANDS.find(([lo, hi]) => (d || 0) >= lo && (d || 0) < hi) || DD_BANDS[2])[2];

const DDBookHealth = ({ reports, dateIso }) => {
  const rows = (reports || []).map((r) => {
    const st = r.stock || {};
    const grp = (list, key) => DD_BANDS.map(([, , label]) => [label, (list || []).filter((d) => ddBand(d[key]) === label).sort((a, b) => (b.fees || 0) - (a.fees || 0))]).filter(([, l]) => l.length);
    const fees = (l) => (l || []).reduce((a, d) => a + (d.fees || 0), 0);
    return { r, quiet: grp(st.quiet, 'days_quiet'), stuck: grp(st.stuck, 'days_in_stage'),
      na: (st.missing_next_action || []).slice().sort((a, b) => (b.fees || 0) - (a.fees || 0)),
      fees: fees(st.quiet) + fees(st.stuck) + fees(st.missing_next_action),
      total: (st.quiet || []).length + (st.stuck || []).length + (st.missing_next_action || []).length };
  }).sort((a, b) => b.fees - a.fees);
  if (!rows.length) return <div style={{ fontSize: 12.5, color: 'var(--text-3)' }}>Generate the day first.</div>;
  const Deal = ({ d, extra }) => (
    <DDRow key={d.engagement_id}>
      <DDLink id={d.engagement_id}>{d.name}</DDLink>
      {d.stage && <Pill hue="slate" sz="sm">{d.stage}</Pill>}
      {d.fees != null && <span style={{ fontSize: 11.5, color: 'var(--text-3)' }}>{ddMoney(d.fees)}</span>}
      {extra}
    </DDRow>
  );
  return (
    <div>
      <div style={{ fontSize: 12, color: 'var(--text-3)', marginBottom: 12, maxWidth: 820, lineHeight: 1.5 }}>
        <window.HelpTip title="What counts" style={{ margin: '0 6px 0 0' }}
          text="Live deals past the D/S call that are quiet (no note from anyone in 30+ days), stuck (older in its stage than 2× the stage median, minimum 30 days), or have no next action. Buyer-side shells and on-hold deals are excluded." />
        <span onClick={() => window.AtlasApi.downloadBookHealthHtml(dateIso)} style={{ color: 'var(--accent-text)', cursor: 'pointer' }}>Download the email version</span>.
      </div>
      {rows.map(({ r, quiet, stuck, na, fees, total }) => (
        <div key={r.member.id} style={{ border: '1px solid var(--border)', borderRadius: 8, background: 'var(--surface)', padding: '12px 14px', marginBottom: 12 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
            <span style={{ fontSize: 14, fontWeight: 650, color: 'var(--text)' }}>{r.member.name}</span>
            <span style={{ fontSize: 12, color: total ? HUE.rose.dot : 'var(--text-4)' }}>{total} flagged · {ddMoney(fees) || '$0'}</span>
            <span style={{ fontSize: 11.5, color: 'var(--text-4)' }}>{(r.stock?.quiet || []).length} quiet · {(r.stock?.stuck || []).length} stuck · {na.length} without next action</span>
          </div>
          {total === 0 && <div style={{ fontSize: 12.5, color: 'var(--text-3)', marginTop: 6 }}>Clean book — nothing flagged.</div>}
          {quiet.map(([label, list]) => (
            <DDSec key={'q' + label} title={`Quiet · ${label}`} count={list.length}>
              {list.map((d) => <Deal key={d.engagement_id} d={d} extra={<span style={{ fontSize: 11, color: (d.days_quiet || 0) > 180 ? HUE.rose.dot : HUE.amber.dot }}>{d.days_quiet}d since last note</span>} />)}
            </DDSec>
          ))}
          {stuck.map(([label, list]) => (
            <DDSec key={'s' + label} title={`Stuck · ${label}`} count={list.length}>
              {list.map((d) => <Deal key={d.engagement_id} d={d} extra={<span style={{ fontSize: 11, color: HUE.rose.dot }}>{d.days_in_stage}d in stage{d.stage_median_days != null ? ` · median ${d.stage_median_days}d` : ''}</span>} />)}
            </DDSec>
          ))}
          {na.length > 0 && (
            <DDSec title="No next action" count={na.length}>
              {na.map((d) => <Deal key={d.engagement_id} d={d} />)}
            </DDSec>
          )}
        </div>
      ))}
    </div>
  );
};

// Cleanup tab — the mission leaderboard, live. Two lists: the people doing
// the cleaning (their own edits, their own book), and the deactivated
// dealmakers whose books others are cleaning. Every number opens the rows
// behind it.
const DD_CLEAN_COLS = [['engagements_touched', 'Updated', 'updated'], ['holds', 'Held', 'held'], ['exits', 'Exited', 'exited'],
  ['next_actions_set', 'Next actions', 'next_actions'], ['stage_moves', 'Stage moves', 'stage_moves'],
  ['reassigned', 'Reassigned', 'reassigned'], ['archived', 'Archived', 'archived']];

const DDCleanupDrill = ({ drill, onClose }) => {
  const [data, setData] = React.useState(null);
  const [err, setErr] = React.useState('');
  React.useEffect(() => {
    if (!drill) return;
    let on = true;
    setData(null); setErr('');
    window.AtlasApi.dailyReportsCleanupDetail({ member_id: drill.memberId, metric: drill.metric, window: drill.window, book: drill.book ? 'true' : undefined })
      .then((d) => { if (on) setData(d); })
      .catch((e) => { if (on) setErr(e?.detail || e?.message || 'Failed to load'); });
    return () => { on = false; };
  }, [drill]);
  if (!drill) return null;
  const sub = drill.window === 'today' ? 'last weekday' : `since ${data ? window.fmtDate(data.since) : 'mission start'}`;
  return (
    <window.Drawer open onClose={onClose} width={560} title={`${drill.label} — ${drill.memberName}`}
      subtitle={data ? `${data.total} ${data.kind === 'deals' ? 'deal' : 'change'}${data.total === 1 ? '' : 's'}${data.engagements != null ? ` on ${data.engagements} engagement${data.engagements === 1 ? '' : 's'}` : ''}${data.kind === 'deals' ? ' · as of now' : ` · ${sub}`}` : sub}>
      {err && <div style={{ padding: 14, fontSize: 12.5, color: HUE.rose.dot }}>{err}</div>}
      {!data && !err && <div style={{ padding: 14 }}><window.SkeletonRows rows={5} /></div>}
      {data && data.items.length === 0 && <div style={{ padding: 14, fontSize: 12.5, color: 'var(--text-3)' }}>Nothing in this window.</div>}
      {data && data.items.map((it, i) => (
        <div key={i} style={{ padding: '7px 14px', borderBottom: '1px solid var(--border)' }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
            <span onClick={() => { onClose(); window.AtlasNav?.goToDeal?.(it.engagement_id); }}
              style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--accent-text)', cursor: 'pointer', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{it.name || '(untitled)'}</span>
            {it.stage && <Pill hue="slate" sz="sm">{it.stage}</Pill>}
            {it.fees != null && <span style={{ fontSize: 11, color: 'var(--text-4)' }}>{ddMoney(it.fees)}</span>}
            {it.at && <span style={{ fontSize: 11, color: 'var(--text-4)', whiteSpace: 'nowrap' }}>{window.fmtDate(it.at)} {ddTime(it.at)}</span>}
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 2 }}>
            {it.text}{it.by && drill.book ? <span style={{ color: 'var(--text-4)' }}> · by {it.by}</span> : null}
            {it.company && it.company !== it.name ? <span style={{ color: 'var(--text-4)' }}> · {it.company}</span> : null}
          </div>
        </div>
      ))}
    </window.Drawer>
  );
};

const DDCleanup = ({ since }) => {
  const [data, setData] = React.useState(null);
  const [err, setErr] = React.useState('');
  const [drill, setDrill] = React.useState(null);
  React.useEffect(() => {
    let on = true;
    setData(null); setErr('');
    window.AtlasApi.dailyReportsCleanup(since).then((d) => { if (on) setData(d); }).catch((e) => { if (on) setErr(e?.detail || e?.message || 'Failed to load'); });
    return () => { on = false; };
  }, [since]);
  if (err) return <div style={{ fontSize: 12.5, color: HUE.rose.dot }}>{err}</div>;
  if (!data) return <div style={{ fontSize: 12.5, color: 'var(--text-3)' }}>Loading…</div>;
  const th = (l, right = true, hint) => <th key={l} title={hint || ''} style={{ textAlign: right ? 'right' : 'left', padding: '7px 10px', fontSize: 10.5, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--text-4)', fontWeight: 600, whiteSpace: 'nowrap', cursor: hint ? 'help' : 'default' }}>{l}</th>;
  // A clickable number: opens the drill-down for (member, metric, window).
  const num = (v, open, opts = {}) => (
    <td style={{ padding: '6px 10px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
      <span onClick={v ? open : undefined}
        style={{ color: !v ? 'var(--text-4)' : opts.bad ? HUE.rose.dot : 'var(--text)', cursor: v ? 'pointer' : 'default', textDecoration: v ? 'underline dotted var(--border-strong)' : 'none', textUnderlineOffset: 3 }}>
        {opts.money ? (v ? ddMoney(v) : '—') : (v ?? 0)}
      </span>
    </td>
  );
  const nameCell = (m) => (
    <td style={{ padding: '6px 10px', fontWeight: 600, color: 'var(--text)', whiteSpace: 'nowrap', cursor: 'pointer' }} onClick={() => window.AtlasNav?.goToUser?.(m.id, m.name)}>{m.name}</td>
  );
  const roleCell = (m) => (
    <td style={{ padding: '6px 10px', color: 'var(--text-4)', whiteSpace: 'nowrap' }}>{(m.role || '').replace('_', ' ')}{m.team_name ? ` · ${m.team_name}` : ''}</td>
  );
  const tableStyle = { borderCollapse: 'collapse', width: '100%', fontSize: 12.5 };
  const box = { overflowX: 'auto', border: '1px solid var(--border)', borderRadius: 8, background: 'var(--surface)', marginBottom: 18 };
  const secTitle = (t) => <div style={{ fontSize: 13, fontWeight: 650, color: 'var(--text)', margin: '4px 0 6px' }}>{t}</div>;
  return (
    <div>
      {secTitle('Cleaning')}
      <div style={box}>
        <table style={tableStyle}>
          <thead><tr>
            {th('Member', false)}{th('Role', false)}
            {DD_CLEAN_COLS.map(([, l]) => th(l, true, `${l} since the start · (+ last weekday)`))}
            {th('Live deals', true, 'Live seller engagements as dealmaker of record')}
            {th('Still flagged', true, 'Their own live deals past the D/S call with no/overdue next action or quiet 30+ days')}
            {th('$ flagged')}
          </tr></thead>
          <tbody>
            {data.rows.length === 0 && <tr><td colSpan={12} style={{ padding: 14, color: 'var(--text-3)' }}>No cleanup roster configured — add people in Settings.</td></tr>}
            {data.rows.map((x) => (
              <tr key={x.member.id} style={{ borderTop: '1px solid var(--border)' }}>
                {nameCell(x.member)}{roleCell(x.member)}
                {DD_CLEAN_COLS.map(([k, l, metric]) => (
                  <td key={k} style={{ padding: '6px 10px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                    <span onClick={x.since_start[k] ? () => setDrill({ memberId: x.member.id, memberName: x.member.name, metric, window: 'since', label: l }) : undefined}
                      style={{ color: (x.since_start[k] || 0) === 0 ? 'var(--text-4)' : 'var(--text)', cursor: x.since_start[k] ? 'pointer' : 'default', textDecoration: x.since_start[k] ? 'underline dotted var(--border-strong)' : 'none', textUnderlineOffset: 3 }}>
                      {x.since_start[k] || 0}
                    </span>
                    {(x.today[k] || 0) > 0 && (
                      <span onClick={() => setDrill({ memberId: x.member.id, memberName: x.member.name, metric, window: 'today', label: `${l} · last weekday` })}
                        style={{ fontSize: 10.5, color: HUE.green.dot, cursor: 'pointer', marginLeft: 3 }}>(+{x.today[k]})</span>
                    )}
                  </td>
                ))}
                {num(x.live_engagements, () => setDrill({ memberId: x.member.id, memberName: x.member.name, metric: 'live', window: 'since', label: 'Live deals' }))}
                {num(x.flagged, () => setDrill({ memberId: x.member.id, memberName: x.member.name, metric: 'flagged', window: 'since', label: 'Still flagged' }), { bad: true })}
                {num(x.flagged_fees, () => setDrill({ memberId: x.member.id, memberName: x.member.name, metric: 'flagged', window: 'since', label: 'Still flagged' }), { bad: true, money: true })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {secTitle('Deactivated')}
      <div style={box}>
        <table style={tableStyle}>
          <thead><tr>
            {th('Member', false)}{th('Role', false)}
            {th('Live deals', true, 'Live seller engagements still under their name')}
            {th('Deals cleaned', true, 'Their engagements edited by anyone since the start')}
            {th('Edits', true, 'Field edits on their deals since the start')}
            {th('Stage moves')}{th('Exited')}
            {th('Cleaned by', false)}
            {th('Still flagged', true, 'Their live deals past the D/S call with no/overdue next action or quiet 30+ days')}
            {th('$ flagged')}
          </tr></thead>
          <tbody>
            {data.deactivated.length === 0 && <tr><td colSpan={10} style={{ padding: 14, color: 'var(--text-3)' }}>No deactivated dealmakers configured.</td></tr>}
            {data.deactivated.map((x) => (
              <tr key={x.member.id} style={{ borderTop: '1px solid var(--border)' }}>
                {nameCell(x.member)}{roleCell(x.member)}
                {num(x.live_engagements, () => setDrill({ memberId: x.member.id, memberName: x.member.name, metric: 'live', window: 'since', label: 'Live deals' }))}
                {num(x.book_engagements_touched, () => setDrill({ memberId: x.member.id, memberName: x.member.name, metric: 'book', window: 'since', label: 'Deals cleaned', book: true }))}
                {num(x.book_edits, () => setDrill({ memberId: x.member.id, memberName: x.member.name, metric: 'book', window: 'since', label: 'Edits on their deals', book: true }))}
                {num(x.book_stage_moves, () => setDrill({ memberId: x.member.id, memberName: x.member.name, metric: 'stage_moves', window: 'since', label: 'Stage moves on their deals', book: true }))}
                {num(x.book_exits, () => setDrill({ memberId: x.member.id, memberName: x.member.name, metric: 'exited', window: 'since', label: 'Exits on their deals', book: true }))}
                <td style={{ padding: '6px 10px', color: 'var(--text-3)', fontSize: 12 }}>{(x.cleaned_by || []).join(', ') || <span style={{ color: 'var(--text-4)' }}>nobody yet</span>}</td>
                {num(x.flagged, () => setDrill({ memberId: x.member.id, memberName: x.member.name, metric: 'flagged', window: 'since', label: 'Still flagged' }), { bad: true })}
                {num(x.flagged_fees, () => setDrill({ memberId: x.member.id, memberName: x.member.name, metric: 'flagged', window: 'since', label: 'Still flagged' }), { bad: true, money: true })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div style={{ fontSize: 11.5, color: 'var(--text-4)' }}>
        Flagged deals can be cleaned in bulk on <span onClick={() => window.AtlasNav?.switchView?.('cleanup')} style={{ color: 'var(--accent-text)', cursor: 'pointer' }}>Needs cleaning → Stale deals</span>.
      </div>
      <DDCleanupDrill drill={drill} onClose={() => setDrill(null)} />
    </div>
  );
};

const DealmakerDailyView = ({ switcher = null } = {}) => {
  const [dateIso, setDateIso] = React.useState(() => {
    const m = (window.location.hash || '').match(/[?&]date=(\d{4}-\d{2}-\d{2})/);
    return m ? m[1] : ddPrevWeekday(ddTodayIso());
  });
  const [tab, setTab] = React.useState(() => {
    const m = (window.location.hash || '').match(/[?&]tab=(daily|book|cleanup)/);
    return m ? m[1] : 'daily';
  });
  const [data, setData] = React.useState(null);
  const [err, setErr] = React.useState('');
  const [busy, setBusy] = React.useState('');
  const [toast, setToast] = React.useState('');
  const [cfgOpen, setCfgOpen] = React.useState(false);
  const [dates, setDates] = React.useState([]);
  const [expanded, setExpanded] = React.useState(() => new Set());   // member ids open; collapsed by default
  const toggleMember = (id) => setExpanded((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const today = ddTodayIso();

  const load = React.useCallback(() => {
    let on = true;
    setErr('');
    window.AtlasApi.dailyReports(dateIso)
      .then((d) => { if (on) setData(d); })
      .catch((e) => { if (on) setErr(e?.detail || e?.message || 'Failed to load'); });
    return () => { on = false; };
  }, [dateIso]);
  React.useEffect(() => { setData(null); return load(); }, [load]);
  React.useEffect(() => {
    window.AtlasApi.dailyReportDates().then((d) => setDates((d.dates || []).map((x) => x.report_date))).catch(() => {});
  }, [data]);
  React.useEffect(() => {
    // Refine our own section hash so the day survives a reload / can be shared.
    const q = [dateIso !== ddPrevWeekday(today) ? `date=${dateIso}` : null, tab !== 'daily' ? `tab=${tab}` : null].filter(Boolean).join('&');
    const want = q ? `#/daily-reports?${q}` : '#/daily-reports';
    if ((window.location.hash || '').startsWith('#/daily-reports') && window.location.hash !== want) {
      try { history.replaceState(null, '', want); } catch (_) {}
    }
  }, [dateIso, today, tab]);

  const flash = (msg) => { setToast(msg); setTimeout(() => setToast(''), 4500); };
  const run = async (label, fn, okMsg) => {
    setBusy(label); setErr('');
    try { const res = await fn(); if (okMsg) flash(typeof okMsg === 'function' ? okMsg(res) : okMsg); load(); }
    catch (e) { setErr(e?.detail || e?.message || `${label} failed`); }
    finally { setBusy(''); }
  };
  const generate = (memberId) => run('generate', () => window.AtlasApi.dailyReportsGenerate(dateIso, memberId), memberId ? 'Recomputed.' : 'Report regenerated.');
  const send = (toMe) => run('send', () => window.AtlasApi.dailyReportsSend(dateIso, toMe), (res) => {
    const bits = [];
    if (res.slack_sent) bits.push(`${res.slack_sent} Slack DM${res.slack_sent === 1 ? '' : 's'}`);
    if (res.email_sent) bits.push(`${res.email_sent} email${res.email_sent === 1 ? '' : 's'}`);
    const fails = [...(res.slack_failed || []), ...(res.email_failed || [])];
    return (bits.length ? `Sent: ${bits.join(', ')}.` : 'Nothing was sent.') + (fails.length ? ` Problems: ${fails.join(' · ')}` : '');
  });

  const reports = data?.reports || [];
  const generatedAt = reports.reduce((acc, r) => (r.generated_at && (!acc || r.generated_at > acc) ? r.generated_at : acc), null);
  const partial = reports.some((r) => r.window?.partial);
  const cfg = data?.config || {};
  const hasRecipients = (cfg.notify || []).length > 0 || (cfg.email_to || []).length > 0;
  const isWeekend = [0, 6].includes(new Date(dateIso + 'T12:00:00').getDay());

  return (
    // The app's main pane is overflow:hidden (app.jsx) — each view owns its
    // scrolling, so this wrapper is the page's scroll container.
    <div style={{ height: '100%', overflowY: 'auto' }}>
    <div style={{ padding: '18px 22px', maxWidth: 1180 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 2, flexWrap: 'wrap' }}>
        <h1 style={{ fontSize: 17, fontWeight: 650, margin: 0, color: 'var(--text)' }}>{switcher ? 'Team activity' : 'Daily reports'}</h1>
        {switcher}
        <div style={{ flex: 1 }} />
        <Btn size="sm" variant="ghost" onClick={() => setDateIso(ddShiftDay(dateIso, -1))} title="Previous day">‹</Btn>
        <input type="date" value={dateIso} max={today} onChange={(e) => e.target.value && setDateIso(e.target.value)}
          list="dd-dates"
          style={{ fontSize: 12.5, padding: '5px 8px', border: '1px solid var(--border)', borderRadius: 5, background: 'var(--surface)', color: 'var(--text)' }} />
        <datalist id="dd-dates">{dates.map((d) => <option key={d} value={d} />)}</datalist>
        <Btn size="sm" variant="ghost" onClick={() => setDateIso(ddShiftDay(dateIso, 1))} disabled={dateIso >= today} title="Next day">›</Btn>
        <Btn size="sm" variant="ghost" onClick={() => setDateIso(ddPrevWeekday(today))} disabled={dateIso === ddPrevWeekday(today)} title="The day the next morning send covers">Latest</Btn>
        <Btn size="sm" variant="ghost" onClick={() => setDateIso(today)} disabled={dateIso === today} title="Today so far">Today</Btn>
        <span style={{ width: 8 }} />
        <Btn size="sm" variant="outline" onClick={() => generate()} disabled={!!busy}>{busy === 'generate' ? 'Computing…' : (reports.length ? 'Refresh' : 'Generate')}</Btn>
        <Btn size="sm" variant="ghost" onClick={() => window.AtlasApi.downloadDailyReportHtml(dateIso)} disabled={!reports.length} title="Download the email version of this day">Email preview</Btn>
        <Btn size="sm" variant="ghost" onClick={() => send(true)} disabled={!!busy || !reports.length} title="Send only to yourself (Slack DM, and the email when email is on) to see what recipients will get">Send to me</Btn>
        <Btn size="sm" variant="ghost" onClick={() => send(false)} disabled={!!busy || !reports.length || !hasRecipients}
          title={hasRecipients ? 'Send this day to the configured recipients now' : 'Add recipients in Settings first'}>Send to recipients</Btn>
        <Btn size="sm" variant="ghost" onClick={() => setCfgOpen(true)}>Settings</Btn>
      </div>
      <div style={{ display: 'flex', gap: 2, marginBottom: 10, borderBottom: '1px solid var(--border)' }}>
        {[['daily', 'Daily'], ['book', 'Book health'], ['cleanup', 'Cleanup']].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} style={{ background: 'none', border: 'none', borderBottom: tab === k ? '2px solid var(--text)' : '2px solid transparent', padding: '6px 10px', fontSize: 12.5, fontWeight: tab === k ? 650 : 500, color: tab === k ? 'var(--text)' : 'var(--text-3)', cursor: 'pointer' }}>{l}</button>
        ))}
      </div>
      {tab === 'daily' && !hasRecipients && data && (
        <div style={{ fontSize: 12, color: HUE.amber.dot, marginBottom: 10 }}>
          No recipients configured — nothing goes out automatically. Add them in Settings.
        </div>
      )}
      {tab === 'cleanup' && <DDCleanup since={cfg.cleanup_since} />}
      {tab === 'book' && data && <DDBookHealth reports={reports} dateIso={dateIso} />}

      {toast && <div style={{ fontSize: 12.5, color: 'var(--text-2)', background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 6, padding: '8px 12px', marginBottom: 10 }}>{toast}</div>}
      {err && <div style={{ fontSize: 12.5, color: HUE.rose.dot, padding: '8px 0' }}>{String(err)}</div>}
      {!data && !err && <div style={{ fontSize: 12.5, color: 'var(--text-3)', padding: '12px 0' }}>Loading…</div>}

      {tab === 'daily' && data && reports.length === 0 && (
        <div style={{ border: '1px dashed var(--border-strong)', borderRadius: 8, padding: '22px 18px', textAlign: 'center', color: 'var(--text-3)', fontSize: 12.5 }}>
          <div style={{ fontSize: 13.5, color: 'var(--text-2)', fontWeight: 600, marginBottom: 4 }}>No report for {ddLongDate(dateIso)}{isWeekend ? ' (a weekend)' : ''}</div>
          Generate it now to see the day so far.
          <div style={{ marginTop: 10 }}><Btn size="sm" variant="accent" onClick={() => generate()} disabled={!!busy}>{busy === 'generate' ? 'Computing…' : 'Generate now'}</Btn></div>
        </div>
      )}

      {tab === 'daily' && reports.length > 0 && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 11.5, color: 'var(--text-4)', marginBottom: 8 }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-2)' }}>{reports[0]?.window?.label || ddLongDate(dateIso)}</span>
            {generatedAt && <span>generated {ddTime(generatedAt)}{partial ? ' · covers the span so far' : ''}</span>}
            {data.state?.last_sent === dateIso && <Pill hue="green" sz="sm" dot>sent automatically</Pill>}
            {reports.some((r) => r.ai) && <span title="hover a line's AI mark for the rule-based version">AI summaries</span>}
          </div>

          <div style={{ overflowX: 'auto', marginBottom: 14, border: '1px solid var(--border)', borderRadius: 8, background: 'var(--surface)' }}>
            <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 12.5 }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left', padding: '7px 10px', fontSize: 10.5, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--text-4)', fontWeight: 600 }}>Dealmaker</th>
                  {DD_BENCH.map(([k, l, , hint]) => <th key={k} title={hint || ''} style={{ textAlign: 'right', padding: '7px 10px', fontSize: 10.5, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--text-4)', fontWeight: 600, whiteSpace: 'nowrap', cursor: hint ? 'help' : 'default' }}>{l}</th>)}
                </tr>
              </thead>
              <tbody>
                {reports.map((r) => (
                  <tr key={r.member.id} style={{ borderTop: '1px solid var(--border)' }}>
                    <td style={{ padding: '6px 10px', fontWeight: 600, color: 'var(--text)', whiteSpace: 'nowrap', cursor: 'pointer' }}
                      title="Expand this person's card" onClick={() => setExpanded((s) => new Set([...s, r.member.id]))}>{r.member.name}</td>
                    {DD_BENCH.map(([k, , flag, , fmt]) => {
                      const v = r.counts?.[k] ?? 0;
                      const bad = !!flag && v > 0;
                      return <td key={k} style={{ padding: '6px 10px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: v === 0 ? 'var(--text-4)' : bad ? HUE.rose.dot : 'var(--text)' }}>{fmt === 'money' ? (v ? ddMoney(v) : '—') : v}</td>;
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, margin: '4px 0 6px' }}>
            <span style={{ fontSize: 10.5, fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--text-4)' }}>Per dealmaker</span>
            <span style={{ fontSize: 11, color: 'var(--text-4)' }}>click a row to expand</span>
            <div style={{ flex: 1 }} />
            <button onClick={() => setExpanded(new Set(reports.map((r) => r.member.id)))} style={{ fontSize: 11, color: 'var(--text-3)', background: 'none', border: 'none', cursor: 'pointer' }}>Expand all</button>
            <button onClick={() => setExpanded(new Set())} style={{ fontSize: 11, color: 'var(--text-3)', background: 'none', border: 'none', cursor: 'pointer' }}>Collapse all</button>
          </div>
          {reports.map((r) => <DDMemberCard key={r.member.id} r={r} busy={!!busy} onRecompute={(id) => generate(id)}
            open={expanded.has(r.member.id)} onToggle={() => toggleMember(r.member.id)} />)}
        </>
      )}

      <DDConfigDrawer open={cfgOpen} onClose={() => setCfgOpen(false)} initial={cfg}
        emailStatus={data?.email_status} state={data?.state}
        onSaved={() => { flash('Settings saved.'); load(); }} />
    </div>
    </div>
  );
};

window.DealmakerDailyView = DealmakerDailyView;
